import type { UISelectedEntityState } from '@sorrem-client/types';
import styles from './EntityInfoPanel.module.scss';

interface EntityInfoPanelProps {
    entityInfo: UISelectedEntityState | null;
}

function formatWorldCoord(value: number): string {
    const rounded = Number(value.toFixed(2));
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

export function EntityInfoPanel({ entityInfo }: EntityInfoPanelProps): JSX.Element {
    if (!entityInfo) {
        return (
            <section className={styles.panel} aria-label="Entity information">
                <div className={styles.header}>
                    <h3 className={styles.title}>Entity</h3>
                    <span className={styles.meta}>Debug entity details</span>
                </div>
                <div className={styles.rows}>
                    <div className={styles.row}>
                        <span>Status</span>
                        <span>Select a game object</span>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.panel} aria-label="Entity information">
            <div className={styles.header}>
                <h3 className={styles.title}>Entity</h3>
                <span className={styles.meta}>Debug entity details</span>
            </div>

            <div className={styles.rows}>
                <div className={styles.row}>
                    <span>Name</span>
                    <span>{entityInfo.name}</span>
                </div>
                <div className={styles.row}>
                    <span>ID</span>
                    <span>{entityInfo.id}</span>
                </div>
                <div className={styles.row}>
                    <span>Type</span>
                    <span>{entityInfo.type}</span>
                </div>
                <div className={styles.row}>
                    <span>Model</span>
                    <span>{entityInfo.characterModelId ?? '(none)'}</span>
                </div>
                <div className={styles.row}>
                    <span>World Bounds</span>
                    <span>({formatWorldCoord(entityInfo.x)}, {formatWorldCoord(entityInfo.y)})</span>
                </div>
                {entityInfo.worldAnchor && (
                    <div className={styles.row}>
                        <span>World Anchor</span>
                        <span>
                            ({formatWorldCoord(entityInfo.worldAnchor.x)}, {formatWorldCoord(entityInfo.worldAnchor.y)})
                        </span>
                    </div>
                )}
                <div className={styles.row}>
                    <span>Bounds Size</span>
                    <span>{formatWorldCoord(entityInfo.width)}x{formatWorldCoord(entityInfo.height)}</span>
                </div>
                {entityInfo.facing && (
                    <div className={styles.row}>
                        <span>Facing</span>
                        <span>
                            ({entityInfo.facing.x.toFixed(2)}, {entityInfo.facing.y.toFixed(2)}) /{' '}
                            {(Math.atan2(entityInfo.facing.y, entityInfo.facing.x) * (180 / Math.PI)).toFixed(0)} deg
                        </span>
                    </div>
                )}
                {entityInfo.velocity && (
                    <div className={styles.row}>
                        <span>Velocity</span>
                        <span>({entityInfo.velocity.x.toFixed(2)}, {entityInfo.velocity.y.toFixed(2)})</span>
                    </div>
                )}
                {entityInfo.animation && (
                    <>
                        <div className={styles.row}>
                            <span>Animation</span>
                            <span>{entityInfo.animation.baseId}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Resolved</span>
                            <span>{entityInfo.animation.resolvedId}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Frame</span>
                            <span>{entityInfo.animation.frameIndex}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Speed</span>
                            <span>{entityInfo.animation.speedMultiplier.toFixed(2)}</span>
                        </div>
                    </>
                )}
                {entityInfo.movement && (
                    <>
                        <div className={styles.row}>
                            <span>Tile</span>
                            <span>({entityInfo.movement.tileX}, {entityInfo.movement.tileY})</span>
                        </div>
                        <div className={styles.row}>
                            <span>Moving</span>
                            <span>{entityInfo.movement.isMoving ? 'yes' : 'no'}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Path Nodes</span>
                            <span>{entityInfo.movement.pathLength}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Target Dist</span>
                            <span>{entityInfo.movement.targetDistanceTiles.toFixed(2)}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Tile Walkable</span>
                            <span>{entityInfo.movement.isTileWalkable ? 'yes' : 'no'}</span>
                        </div>
                        <div className={styles.row}>
                            <span>Anchor Collision</span>
                            <span>{entityInfo.movement.isCollidingAtAnchor ? 'yes' : 'no'}</span>
                        </div>
                    </>
                )}
                <div className={styles.row}>
                    <span>Properties</span>
                    <span>{entityInfo.propertyCount}</span>
                </div>
            </div>
        </section>
    );
}
